import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { callContract } from '../contractClient'
import { contracts } from '../../../../shared/features/platform.contracts'
import { useBranding } from './BrandingContext'
import useVisibilityRefresh from '../hooks/useVisibilityRefresh'
import {
  ENTITLEMENT_KEYS,
  ENTITLEMENTS_BY_KEY,
  defaultEntitlementFeatureMap,
  isCanonicalEntitlementKey,
} from '@ops/entitlements'

const reportedUnknownKeys = new Set()

export function assertCanonicalEntitlementKey(featureKey) {
  if (isCanonicalEntitlementKey(featureKey)) return true
  if (!reportedUnknownKeys.has(featureKey)) {
    reportedUnknownKeys.add(featureKey)
    // Loud in dev tooling (tests, console) and CI test runs. Fail-closed in
    // prod. A non-canonical gate key is a product regression that must not
    // ship silently.
    console.error(
      `[entitlements] non-canonical entitlement key "${featureKey}". ` +
        `Gated surfaces must reference a canonical key from @ops/entitlements.`,
    )
  }
  return false
}

const EntitlementContext = createContext(null)

function allFeaturesDisabled() {
  return ENTITLEMENT_KEYS.reduce((acc, key) => {
    acc[key] = false
    return acc
  }, {})
}

function normalizeCustomerFeatures(raw) {
  const map = defaultEntitlementFeatureMap()
  if (!raw || typeof raw !== 'object') return map
  for (const key of Object.keys(raw)) {
    if (ENTITLEMENTS_BY_KEY[key]) {
      map[key] = !!raw[key]
    }
  }
  return map
}

const INITIAL_STATE = {
  version: 'bootstrap_unavailable',
  updated_at: null,
  source: 'bootstrap_unavailable',
  features: allFeaturesDisabled(),
  limits: { max_hosts: null },
  // Top-level snapshot flag (PR #55), NOT an entitlement. Defaults to
  // shown — the License page is a display preference, not a gated
  // surface, so it does not fail closed.
  show_license_page: true,
}

function normalizePollSeconds(rawValue) {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10)
  if (!Number.isFinite(parsed)) return 120
  return Math.max(30, Math.min(600, parsed))
}

export function EntitlementProvider({ children }) {
  const branding = useBranding()
  const pollSeconds = normalizePollSeconds(branding?.licensing?.pollSeconds)
  const [state, setState] = useState(INITIAL_STATE)
  const [loading, setLoading] = useState(true)
  const [lastError, setLastError] = useState(null)

  const fetchState = useCallback(async (forceRefresh = false, useBootstrapCache = false, signal) => {
    try {
      const res = await callContract(contracts.entitlementsState, {
        ...(forceRefresh ? { query: { refresh: 1 } } : {}),
        config: {
          ...(!useBootstrapCache ? { sessionBootstrapCache: false } : {}),
          ...(signal && !useBootstrapCache ? { signal } : {}),
        },
      })
      if (signal?.aborted) return
      const payload = res.data?.entitlements || {}
      setState({
        version: payload.version || 'bootstrap_unavailable',
        updated_at: payload.updated_at || null,
        source: payload.source || 'bootstrap_unavailable',
        features: normalizeCustomerFeatures(payload.features),
        limits: {
          max_hosts: Number.parseInt(String(payload?.limits?.max_hosts ?? ''), 10) || null,
        },
        // Absent (pre-#55 admin worker, or no snapshot) → shown.
        show_license_page: payload.show_license_page !== false,
      })
      setLastError(null)
    } catch (error) {
      if (signal?.aborted) return
      setLastError(error)
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [])

  const scheduledRefresh = useVisibilityRefresh({
    refresh: ({ signal, reason }) => fetchState(
      reason !== 'interval',
      reason === 'initial',
      signal,
    ),
    intervalMs: pollSeconds * 1000,
  })

  const isFeatureEnabled = useCallback((featureKey) => {
    if (!featureKey) return true
    if (!assertCanonicalEntitlementKey(featureKey)) return false
    return state.features[featureKey] === true
  }, [state.features])

  const refreshEntitlements = scheduledRefresh

  const value = useMemo(() => ({
    ...state,
    loading,
    lastError,
    isFeatureEnabled,
    refreshEntitlements,
    // camelCase alias for the License-page visibility flag — consumed by
    // the License nav item + route. Not an entitlement; do not route it
    // through isFeatureEnabled.
    showLicensePage: state.show_license_page !== false,
  }), [isFeatureEnabled, lastError, loading, refreshEntitlements, state])

  return (
    <EntitlementContext.Provider value={value}>
      {children}
    </EntitlementContext.Provider>
  )
}

export function useEntitlements() {
  const context = useContext(EntitlementContext)
  if (!context) {
    throw new Error('useEntitlements must be used within an EntitlementProvider')
  }
  return context
}
